import { useEffect, useRef, useState } from 'react'
import { listAccounts } from '../../api/accounts'
import { rsqlContains } from '../../lib/rsql'
import { Icon } from '../../components/icons/Icon'
import type { AccountView } from '../../types/account'

export interface AccountLookupFieldProps {
  id: string
  label: string
  value: { id: string; name: string } | null
  onChange: (value: { id: string; name: string } | null) => void
  /** Exclude this id from results (an account cannot be its own parent). */
  excludeId?: string | undefined
}

/**
 * Minimal accessible combobox over `GET /accounts?filter=name==*term*` — the
 * `parentAccountId` lookup on the New/Edit account form. A generic
 * `EntityLookupField` would be the natural next extraction once a second
 * module needs the same pattern (flagged in the UX report).
 */
export function AccountLookupField({ id, label, value, onChange, excludeId }: AccountLookupFieldProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<AccountView[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Debounced search — the timer only runs while the listbox is open, so a
  // closed/unfocused field never schedules background state updates.
  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults([])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoading(true)
      listAccounts({ filter: rsqlContains('name', query.trim()), limit: 8 })
        .then((page) => {
          if (cancelled) return
          setResults(page.items.filter((a) => a.id !== excludeId))
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, open, excludeId])

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (value) {
    return (
      <div className="iw-field">
        <span className="iw-field-label" id={`${id}-label`}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="iw-pill iw-pill--neutral">{value.name}</span>
          <button type="button" className="iw-btn iw-btn--sm" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="iw-field" ref={containerRef} style={{ position: 'relative' }}>
      <label className="iw-field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        className="iw-input"
        placeholder="Search accounts by name…"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
      />
      {open && (loading || results.length > 0 || query.trim().length > 0) ? (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label={`${label} results`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 'var(--iw-z-dropdown)' as never,
            background: 'var(--iw-surface-raised)',
            border: '1px solid var(--iw-border)',
            borderRadius: 'var(--iw-radius)',
            boxShadow: 'var(--iw-shadow-lg)',
            margin: '4px 0 0',
            padding: 4,
            listStyle: 'none',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Searching…</li>
          ) : results.length === 0 ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>No matches</li>
          ) : (
            results.map((account) => (
              <li key={account.id} role="option" aria-selected="false">
                <button
                  type="button"
                  className="iw-btn iw-btn--ghost iw-btn--sm"
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                  onClick={() => {
                    onChange({ id: account.id, name: account.name })
                    setQuery('')
                    setOpen(false)
                  }}
                >
                  <Icon name="building2" size={13} />
                  {account.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
